import { BillingGroup, BillingGroupTypeEnum, InvitePolicyEnum } from "@clockwise/schema";
// libraries
import * as React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  FormControl,
  IconButton,
  Select,
  MenuItem,
  Popover,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  Checkbox,
} from "@material-ui/core";
import { MoreHoriz } from "@material-ui/icons";
import classNames from "classnames";

import { greys, colors } from "@styles/color.styles";
import { size } from "@styles/type.styles";

import { RedButton } from "../../../red-button";
import { ZonedMoment } from "@clockwise/client-commons/src/util/time-zone";
import { getAbsoluteDate } from "@clockwise/client-commons/src/util/format-time";
import { CwLink } from "../../../cw-link";

import { getValue } from "@util/errorable.util";

import {
  getFullName,
  getSubscriptionFromBillingGroup,
  isSubscriptionProductTypeEnterprise,
  isSubscriptionProductTypeBusiness,
  isSubscriptionProductTypePro,
} from "../billing-groups-members.util";
import {
  MemberPerson,
  SearchPersonStatusResponse,
  BillingGroupRoleEnum,
} from "../billing-groups-members.type";
import { AvatarGeneric } from "../../avatar-generic";
// import { SuggestedDecorator } from "../suggested-decorator";
// import { InviteButton } from "../invite-button";
import { logger } from "@clockwise/client-commons/src/util/logger";

export interface MemberRowProps {
  person: MemberPerson;
  sendingInvite: string;
  sendInvite: (email: string) => void;
  onAdminSelectChange: (
    userId: string,
    billingGroupId: string,
    role: BillingGroupRoleEnum,
    personId: string,
    errorMsg?: string,
  ) => void;
  onRemoveMember: (personIds: string[], billingGroupId: string, errorMsg?: string) => void;
  onAddMember: (personId: string[], billingGroupId: string) => void;
  onRequestToJoinMember: (personId: string, billingGroupId: string, email: string) => void;
  persons: MemberPerson[];
  billingGroups: BillingGroup[];
  isChecked: boolean;
  onSelect: (person: MemberPerson) => void;
  isSelectDisabled: boolean;
}

const useStyles = makeStyles({
  row: {
    borderBottom: "solid 1px #E5E5E5",
  },
  cell__content: {
    display: "flex",
    alignItems: "center",
    border: "none",
    "& > div:last-child": {
      marginLeft: 10,
    },
  },
  cell__name: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 150,
  },
  cell__row: {
    display: "flex",
    alignItems: "center",
  },
  cell__text: {
    color: greys.dark,
  },
  cell__badge: {
    background: colors.peach.darker,
    color: greys.white,
    borderRadius: 2,
    fontSize: 11,
    padding: "2px 5px",
    marginLeft: 5,
  },
  cell__subtext: {
    fontSize: size.md,
    color: greys.slate,
  },
  inviteWrapper: {
    marginLeft: "auto",
    justifyContent: "flex-end",
  },
  link: {
    cursor: "pointer",
    color: colors.blueCool.standard,
    textDecoration: "none",
    transition: "color .3s",
    display: "flex",
    alignItems: "center",
    paddingRight: 13,
    "&:hover": {
      color: colors.blueDusk.darker,
    },
  },
  disabled: {
    color: greys.slate,
    cursor: "not-allowed",
  },
});

export const MemberRow = (props: MemberRowProps) => {
  const { person, isChecked, onSelect, billingGroups, isSelectDisabled } = props;

  const classes = useStyles();
  const name = getFullName({
    givenName: person.givenName,
    familyName: person.familyName,
  });

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false);
  const [dialogBillingGroup, setDialogBillingGroup] = React.useState<BillingGroup | null>(null);

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  // const maybeRenderInviteButton = () => {
  //   if (status === SearchPersonStatusResponse.User) {
  //     return null;
  //   }

  //   return (
  //     <>
  //       <InviteButton
  //         isPending={status === SearchPersonStatusResponse.Invited}
  //         recentlySent={recentlySent}
  //         sending={sendingInvite === targetCalendarId}
  //         onInvite={handleOnInvite}
  //       />
  //     </>
  //   );
  // };

  const getStatusText = () => {
    if (person.recentlySent) {
      return "Invite pending";
    }
    if (person.recentlyRequested) {
      return "Request pending";
    }

    switch (person.status) {
      case SearchPersonStatusResponse.Invited:
        return "Invite pending";
      case SearchPersonStatusResponse.NonUser:
      case SearchPersonStatusResponse.Deactivated:
      case SearchPersonStatusResponse.User:
      default:
        return "";
    }
  };

  const hasPermissionToAdd = (bg: BillingGroup) => {
    const isInviteAnyMember = bg.invitePolicy === InvitePolicyEnum.AnyMember;

    const isAnyMemberAndMember =
      isInviteAnyMember &&
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex(
        (p) => p.orgPerson?.__typename === "OrgPerson" && p.orgPerson.isYou,
      ) >= 0;

    const isAdminOnlyAndAdmin =
      bg.invitePolicy === InvitePolicyEnum.AdminOnly &&
      bg.adminOrgPersons.__typename === "OrgPersonList" &&
      bg.adminOrgPersons.list.findIndex((p) => p.isYou) >= 0;

    const isPersonAlreadyMember =
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex((p) => p.calendarId === person.targetCalendarId) >= 0;

    const isPersonYou = person.isYou;

    const isFreeUserAndCanAddSelf = isInviteAnyMember && !isPersonAlreadyMember && isPersonYou;

    return (
      !isPersonAlreadyMember &&
      (isAnyMemberAndMember || isAdminOnlyAndAdmin || isFreeUserAndCanAddSelf)
    );
  };

  const hasPermissionToRequestToJoin = (bg: BillingGroup) => {
    // member row person is you not in any billing groups
    if (person.isYou && !person.billingGroup) {
      return true;
    }

    const isCurrentUserAlreadyMember =
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex(
        (p) => p.orgPerson?.__typename === "OrgPerson" && p.orgPerson.isYou,
      ) >= 0;

    const isPersonMember =
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex((p) => p.calendarId === person.targetCalendarId) >= 0;

    return isCurrentUserAlreadyMember && !isPersonMember;
  };

  const hasPermissionToRemove = (bg: BillingGroup) => {
    const isProPlan = isProductTypePro(bg);
    const isBusinessPlan = isProductTypeBusiness(bg);
    const isEnterprisePlan = isProductTypeEnterprise(bg);

    const isAdmin =
      bg.adminOrgPersons.__typename === "OrgPersonList"
        ? bg.adminOrgPersons.list.some((orgPerson) => orgPerson.isYou)
        : false;

    const isCurrentUserAlreadyMember =
      bg.manualMembers.__typename === "BillingGroupManualMemberList" &&
      bg.manualMembers.list.findIndex(
        (p) => p.orgPerson?.__typename === "OrgPerson" && p.orgPerson.isYou,
      ) >= 0;
    return (
      bg &&
      ((isEnterprisePlan && isAdmin) ||
        (isBusinessPlan && isAdmin) ||
        (isProPlan && isCurrentUserAlreadyMember))
    );
  };

  const isBillingGroupSso = (billingGroup: BillingGroup) => {
    return billingGroup.type === BillingGroupTypeEnum.Sso;
  };

  const statusText = getStatusText();

  const isProductTypeEnterprise = (bg: BillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);
    return (
      billingGroupSubscription &&
      isSubscriptionProductTypeEnterprise(billingGroupSubscription.subscription)
    );
  };

  const isProductTypeBusiness = (bg: BillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);
    return (
      billingGroupSubscription &&
      isSubscriptionProductTypeBusiness(billingGroupSubscription.subscription)
    );
  };

  const isProductTypePro = (bg: BillingGroup) => {
    const billingGroupSubscription = getSubscriptionFromBillingGroup(bg);

    return (
      billingGroupSubscription &&
      isSubscriptionProductTypePro(billingGroupSubscription.subscription)
    );
  };

  // ~-~-~-~-~-~-~-
  // Callbacks
  // ~-~-~-~-~-~-~-
  const handleAdminSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const eMsg = `cannot select billing group role`;

    if (!person.billingGroup) {
      logger.error(`${eMsg} because no billingGroup`);
      return;
    }

    if (!hasPermissionToRemove(person.billingGroup)) {
      logger.error(`${eMsg} because user is not an admin`);
      return;
    }

    if (!person.userId) {
      logger.error(`${eMsg} because no userId`);
      return;
    }

    const role = event.target.value as BillingGroupRoleEnum;
    const lastAdminError =
      role !== BillingGroupRoleEnum.Admin &&
      person.billingGroup.adminOrgPersons.__typename === "OrgPersonList" &&
      person.billingGroup.adminOrgPersons.list.length <= 1
        ? `You're the only admin. Please assign another admin before you leave this group.`
        : ``;

    props.onAdminSelectChange(
      person.userId,
      person.billingGroup.id,
      role,
      person.personId,
      lastAdminError,
    );
  };

  const handleClickMoreOptions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMoreOptions = () => {
    setAnchorEl(null);
  };

  const closeRemoveDialog = () => {
    setDialogOpen(false);
    setDialogBillingGroup(null);
  };

  const handleRemoveMember = (p: MemberPerson, bg: BillingGroup | null) => {
    const eMsg = `cannot remove member from billing group`;

    if (!bg) {
      logger.error(`${eMsg} because no billingGroup`);
      return;
    }

    if (!hasPermissionToRemove(bg)) {
      logger.error(`${eMsg} because user is not an admin`);
      return;
    }

    const lastAdminError =
      hasPermissionToRemove(bg) &&
      p.isYou &&
      bg.adminOrgPersons.__typename === "OrgPersonList" &&
      bg.adminOrgPersons.list.length <= 1
        ? `You're the only admin. Please assign another admin before you leave this group.`
        : ``;
    props.onRemoveMember([p.personId], bg.id, lastAdminError);
    closeRemoveDialog();
  };

  const handleAddMember = (bg: BillingGroup) => {
    const eMsg = `cannot add member to billing group`;

    if (!hasPermissionToAdd(bg)) {
      logger.error(`${eMsg} because user does not have permission`);
      return;
    }

    props.onAddMember([person.personId], bg.id);
  };

  const handleRequestToJoinMember = (bg: BillingGroup) => {
    props.onRequestToJoinMember(person.personId, bg.id, person.targetCalendarId);
  };

  const handleOnInvite = React.useCallback(() => {
    if (person.recentlySent) {
      return;
    }
    props.sendInvite(person.targetCalendarId);
  }, [person.targetCalendarId]);

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  const getSignUpDateText = () => {
    if (!person.signUpMillis) {
      return "";
    }

    return `${getAbsoluteDate(new ZonedMoment(person.signUpMillis), undefined, undefined, true)}`;
  };

  const getBillingGroupText = () => {
    if (person.status !== SearchPersonStatusResponse.User) {
      return "";
    } else if (!person.billingGroup) {
      return "Free";
    }

    return person.billingGroup.name;
  };

  const maybeRenderAdminSelect = () => {
    if (!person.billingGroup || person.status !== SearchPersonStatusResponse.User) {
      return;
    }

    const showForm =
      isProductTypeEnterprise(person.billingGroup) || isProductTypeBusiness(person.billingGroup);
    const hasRemoveAccess = hasPermissionToRemove(person.billingGroup);

    return showForm ? (
      <FormControl>
        <Tooltip
          title={
            !hasRemoveAccess
              ? `You don't have permission to manage this user. To request changes, please contact a plan admin.`
              : ``
          }
        >
          <Select
            value={person.role}
            onChange={handleAdminSelectChange}
            disabled={!hasRemoveAccess}
            disableUnderline
          >
            <MenuItem value={BillingGroupRoleEnum.Admin}>Admin</MenuItem>
            <MenuItem value={BillingGroupRoleEnum.Member}>Member</MenuItem>
          </Select>
        </Tooltip>
      </FormControl>
    ) : (
      <span> {person.role} </span>
    );
  };

  const maybeRenderInviteResend = () => {
    if (person.recentlySent || person.recentlyRequested) {
      return (
        <div className={classNames([classes.link, classes.disabled])} onClick={handleOnInvite}>
          Sent!
        </div>
      );
    } else if (props.sendingInvite === person.targetCalendarId) {
      return (
        <div className={classNames([classes.link, classes.disabled])} onClick={handleOnInvite}>
          Sending
        </div>
      );
    } else {
      return (
        <CwLink className={classNames([classes.link])} onClick={handleOnInvite}>
          Resend invite
        </CwLink>
      );
    }
  };

  const isPersonInBillingGroup = (bg: BillingGroup, person: MemberPerson) => {
    const members = getValue(bg.manualMembers)?.list ?? [];
    return members.findIndex((p) => p.calendarId === person.targetCalendarId) >= 0;
  };

  const maybeRenderMoreOptions = () => {
    if (person.status === SearchPersonStatusResponse.Invited || person.recentlyRequested) {
      return (
        <div className={classes.cell__row} style={{ flexDirection: "row-reverse" }}>
          {maybeRenderInviteResend()}
        </div>
      );
    } else if (person.status !== SearchPersonStatusResponse.User) {
      return;
    }

    const hasMenuItems = props.billingGroups.some((bg) => {
      // render if user can add, remove, or request
      return (
        !isBillingGroupSso(bg) && // if no SOO
        ((hasPermissionToRemove(bg) && isPersonInBillingGroup(bg, person)) || // if the user has the ability to remove and is in the billing group
          hasPermissionToAdd(bg) ||
          hasPermissionToRequestToJoin(bg))
      );
    });

    const isDisabled = !hasMenuItems;

    return (
      <div className={classes.cell__row} style={{ flexDirection: "row-reverse" }}>
        <Tooltip title={isDisabled ? `` : ``}>
          <div>
            <IconButton onClick={handleClickMoreOptions} disabled={isDisabled}>
              <MoreHoriz />
            </IconButton>
          </div>
        </Tooltip>
        <Popover
          open={!!anchorEl}
          anchorEl={anchorEl}
          onClose={handleCloseMoreOptions}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          disableScrollLock
        >
          {billingGroups.map((bg, i) => {
            if (isBillingGroupSso(bg)) {
              return "";
            } else if (hasPermissionToRemove(bg) && isPersonInBillingGroup(bg, person)) {
              return (
                <MenuItem
                  key={`more-option-menu-item-remove-${i}`}
                  onClick={() => {
                    setDialogBillingGroup(bg);
                    setDialogOpen(true);
                    handleCloseMoreOptions();
                  }}
                >
                  {person.isYou ? `Leave` : `Remove user from`} {bg.name}
                </MenuItem>
              );
            } else if (hasPermissionToAdd(bg)) {
              return (
                <MenuItem
                  key={`more-option-menu-item-add-${i}`}
                  onClick={() => {
                    handleAddMember(bg);
                    handleCloseMoreOptions();
                  }}
                >
                  Add to {bg.name}
                </MenuItem>
              );
            } else if (hasPermissionToRequestToJoin(bg)) {
              return (
                <MenuItem
                  key={`more-option-menu-item-request-${i}`}
                  onClick={() => {
                    handleRequestToJoinMember(bg);
                    handleCloseMoreOptions();
                  }}
                >
                  Request to join {bg.name}
                </MenuItem>
              );
            }
            return "";
          })}
        </Popover>
      </div>
    );
  };

  return (
    <>
      <TableRow className={classes.row}>
        {!isSelectDisabled && (
          <TableCell>
            <Checkbox
              size="small"
              checked={isChecked}
              onChange={() => onSelect(person)}
              style={{ color: isChecked ? colors.green.standard : greys.slate }}
              disabled={isSelectDisabled}
            />
          </TableCell>
        )}
        <TableCell className={classes.cell__name}>
          <div className={classes.cell__content}>
            <AvatarGeneric
              imgUrl={person.externalImageUrl}
              name={name}
              showBadge={person.status === SearchPersonStatusResponse.User}
            />
            <div>
              <div className={classes.cell__row}>
                <div className={classes.cell__text}>{`${name}${person.isYou ? " (You)" : ""}`}</div>
                {statusText ? <div className={classes.cell__badge}>{statusText}</div> : ""}
              </div>
              <div className={classes.cell__subtext}>{person.targetCalendarId}</div>
            </div>
          </div>
        </TableCell>
        <TableCell>{getSignUpDateText()}</TableCell>
        <TableCell>{getBillingGroupText()}</TableCell>
        <TableCell>{maybeRenderAdminSelect()}</TableCell>
        <TableCell>{maybeRenderMoreOptions()}</TableCell>
      </TableRow>
      <Dialog open={dialogOpen}>
        <DialogTitle>
          <Typography variant="h2" style={{ fontSize: 22, fontWeight: "bold" }}>
            {person.isYou
              ? `Leave ${dialogBillingGroup && dialogBillingGroup.name}?`
              : `Remove ${name} from ${dialogBillingGroup && dialogBillingGroup.name}?`}
          </Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
          <Typography variant="body2">
            {person.isYou
              ? `Are you sure you want to remove your own access? You will lose access to all exisiting
            paid features.`
              : `Are you sure you want to remove this person? They will lose access to all exisiting paid features.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => closeRemoveDialog()}>
            Cancel
          </Button>
          <RedButton
            onClick={() => handleRemoveMember(person, dialogBillingGroup)}
            label="Confirm"
          />
        </DialogActions>
      </Dialog>
    </>
  );
};
